// src/pages/public/SubmitProblem.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { 
  ArrowLeft, 
  FileText, 
  AlertTriangle, 
  Send,
  CheckCircle,
  Clock,
  Zap,
  Package,
  Truck,
  Settings,
  Users,
  User,
  Mail,
  Phone,
  MapPin,
  Info,
  Building2,
  Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';

// Industry sectors
const INDUSTRY_SECTORS = [
  { value: 'fmcg', label: 'FMCG' },
  { value: 'agro_processing', label: 'Agro-processing' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'pharmaceutical', label: 'Pharmaceutical' },
  { value: 'textile', label: 'Textile' },
  { value: 'plastics', label: 'Plastics' },
  { value: 'other', label: 'Other' },
];

// Problem categories
const PROBLEM_CATEGORIES = [
  { value: 'downtime', label: 'Downtime', icon: Clock },
  { value: 'energy_waste', label: 'Energy Waste', icon: Zap },
  { value: 'inventory_shrinkage', label: 'Inventory Shrinkage', icon: Package },
  { value: 'quality_control', label: 'Quality Control', icon: CheckCircle },
  { value: 'logistics', label: 'Logistics', icon: Truck },
  { value: 'maintenance', label: 'Maintenance', icon: Settings },
  { value: 'labor_efficiency', label: 'Labor Efficiency', icon: Users },
  { value: 'other', label: 'Other', icon: AlertTriangle },
];

// Urgency levels
const URGENCY_LEVELS = [
  { value: 'low', label: 'Low - Can wait', color: 'bg-green-100 text-green-700' },
  { value: 'medium', label: 'Medium - Within the year', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'high', label: 'High - Immediate', color: 'bg-red-100 text-red-700' },
];

// Simplified steps for public users
const STEPS = [
  { number: 1, title: 'Your Info', description: 'Who you are', icon: User },
  { number: 2, title: 'Problem Details', description: 'What you observed', icon: FileText },
  { number: 3, title: 'Impact', description: 'How it affects operations', icon: AlertTriangle },
];

export default function SubmitProblem() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data matching external_submissions table
  const [formData, setFormData] = useState({
    // Personal Information
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contactTitle: 'Individual Contributor',
    
    // Optional company info (if submitting on behalf of a company)
    companyName: '',
    companyRegistration: '',
    
    // Problem Details
    problemTitle: '',
    problemDescription: '',
    industrySector: '',
    urgency: 'medium' as 'low' | 'medium' | 'high',
    location: '',
    estimatedImpact: '',
    
    // Documents (optional)
    documents: [] as string[],
  });

  const [newDocument, setNewDocument] = useState('');

  const totalSteps = 3;
  const currentStepData = STEPS[currentStep - 1];
  const StepIcon = currentStepData.icon;

  const handleNext = () => {
    setError(null);
    if (!isStepValid()) {
      setError('Please complete all required fields before continuing.');
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setError(null);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const addDocument = () => {
    if (newDocument.trim()) {
      setFormData({
        ...formData,
        documents: [...formData.documents, newDocument.trim()]
      });
      setNewDocument('');
    }
  };

  const removeDocument = (index: number) => {
    setFormData({
      ...formData,
      documents: formData.documents.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);
    
    try {
      // Insert into external_submissions table
      const { data, error: supabaseError } = await supabase
        .from('external_submissions')
        .insert({
          company_name: formData.companyName || 'Individual Submission',
          company_registration: formData.companyRegistration || null,
          contact_name: formData.contactName,
          contact_title: formData.contactTitle,
          contact_email: formData.contactEmail,
          contact_phone: formData.contactPhone,
          problem_title: formData.problemTitle,
          problem_description: formData.problemDescription,
          estimated_impact: formData.estimatedImpact || null,
          urgency: formData.urgency,
          documents: formData.documents.length > 0 ? formData.documents : null,
          status: 'pending'
        })
        .select()
        .single();

      if (supabaseError) throw supabaseError;
      
      toast({
        title: 'Success!',
        description: 'Your problem has been submitted. Our team will review it shortly.',
      });
      
      navigate('/submission-thank-you');
    } catch (error) {
      console.error('Submission error:', error);
      setError('Failed to submit problem. Please try again.');
      toast({
        title: 'Error',
        description: 'Failed to submit problem',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    switch(currentStep) {
      case 1:
        return formData.contactName && 
               formData.contactEmail && 
               formData.contactEmail.includes('@');
      case 2:
        return formData.problemTitle && 
               formData.problemDescription && 
               formData.industrySector && 
               selectedCategories.length > 0;
      case 3:
        return true;
      default:
        return true;
    }
  };

  const getProgressPercentage = () => {
    return (currentStep / totalSteps) * 100;
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header with back button */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.location.href = '/#hub'} 
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Hub
          </Button>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold">Share an Industrial Problem</h1>
            <p className="text-muted-foreground mt-2">
              Have you noticed a challenge in manufacturing, logistics, or operations?
              <br />
              Share it with our community of problem-solvers.
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium">Step {currentStep} of {totalSteps}</span>
            <span className="text-sm text-muted-foreground">{Math.round(getProgressPercentage())}% Complete</span>
          </div>
          <Progress value={getProgressPercentage()} className="h-2" />
        </div>

        {/* Step navigation pills */}
        <div className="flex flex-wrap gap-2 mb-6 justify-center">
          {STEPS.map((step) => {
            const StepIcon = step.icon;
            const isActive = step.number === currentStep;
            const isCompleted = step.number < currentStep;
            
            return (
              <button
                key={step.number}
                onClick={() => {
                  if (step.number < currentStep || isStepValid()) {
                    setCurrentStep(step.number);
                  }
                }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : isCompleted
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                <StepIcon className="h-4 w-4" />
                <span>{step.title}</span>
                {isCompleted && <CheckCircle className="h-3 w-3 ml-1" />}
              </button>
            );
          })}
        </div>

        {/* Error message */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step content */}
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <StepIcon className="h-5 w-5" />
              </div>
              <div>
                <CardTitle>{currentStepData.title}</CardTitle>
                <CardDescription className="text-blue-100">
                  {currentStepData.description}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* STEP 1: Your Info */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <User className="h-5 w-5 text-blue-600" />
                    Your Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        placeholder="e.g., John Mwangi"
                        value={formData.contactName}
                        onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="email"
                          placeholder="john@example.com"
                          value={formData.contactEmail}
                          onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Phone (optional)</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="+254 712 345 678"
                          value={formData.contactPhone}
                          onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Optional Company Section */}
                  <div className="border-t pt-4 mt-2">
                    <h3 className="text-md font-semibold flex items-center gap-2 text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                      Submitting on behalf of a company? (Optional)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                      <div className="space-y-2">
                        <Label>Company Name</Label>
                        <Input
                          placeholder="e.g., Kakuzi PLC"
                          value={formData.companyName}
                          onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Registration Number</Label>
                        <Input
                          placeholder="e.g., CBL/2010/1234"
                          value={formData.companyRegistration}
                          onChange={(e) => setFormData({ ...formData, companyRegistration: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <Alert className="bg-blue-50 border-blue-200">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-700 text-sm">
                      We'll only use your contact info to follow up if we need clarification about the problem.
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            )}

            {/* STEP 2: Problem Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    Tell us about the problem
                  </h3>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      Problem Title <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      placeholder="e.g., Harvesta: Youth Harvesters Initiative - Agricultural Labour Shortage"
                      value={formData.problemTitle}
                      onChange={(e) => setFormData({ ...formData, problemTitle: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Example: "Harvesta: Addressing Agricultural Labour Shortage Through Harvesting-as-a-Service"
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      Problem Description <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      placeholder="Describe what you observed. What's happening? Where? When does it occur? Who is affected? Include details like: seasonal labour shortages, crop damage, youth unemployment, specific value chains (avocado, tomato), export quality impacts."
                      rows={8}
                      value={formData.problemDescription}
                      onChange={(e) => setFormData({ ...formData, problemDescription: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        Industry Sector <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.industrySector}
                        onValueChange={(value) => setFormData({ ...formData, industrySector: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRY_SECTORS.map(sector => (
                            <SelectItem key={sector.value} value={sector.value}>
                              {sector.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        Urgency <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.urgency}
                        onValueChange={(value: any) => setFormData({ ...formData, urgency: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select urgency" />
                        </SelectTrigger>
                        <SelectContent>
                          {URGENCY_LEVELS.map(level => (
                            <SelectItem key={level.value} value={level.value}>
                              <span className={level.color}>{level.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      Problem Categories <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {PROBLEM_CATEGORIES.map(category => {
                        const Icon = category.icon;
                        const isSelected = selectedCategories.includes(category.value);
                        return (
                          <div
                            key={category.value}
                            onClick={() => toggleCategory(category.value)}
                            className={`
                              flex items-center justify-center gap-1.5 p-3 rounded-lg border cursor-pointer transition-all
                              ${isSelected 
                                ? 'border-blue-500 bg-blue-50 text-blue-700' 
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                              }
                            `}
                          >
                            <Icon className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                            <span className="text-xs font-medium">{category.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Location (optional)</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="e.g., Murang'a, Kiambu, Kirinyaga, Meru Counties"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: Impact & Documents */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-blue-600" />
                    Impact & Supporting Documents
                  </h3>
                  
                  <div className="space-y-2">
                    <Label>Estimated Impact (optional)</Label>
                    <Textarea
                      placeholder="If you know, tell us about the impact - e.g., 'KES 3B annual loss across avocado and tomato value chains', '25% post-harvest loss from bruising', '70% youth unemployment in target counties'"
                      rows={4}
                      value={formData.estimatedImpact}
                      onChange={(e) => setFormData({ ...formData, estimatedImpact: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Example from Harvesta: KES 3B total estimated impact across avocado, tomato, and youth unemployment
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Supporting Documents (Google Drive URLs)</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="https://drive.google.com/..."
                        value={newDocument}
                        onChange={(e) => setNewDocument(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addDocument()}
                      />
                      <Button type="button" onClick={addDocument} variant="outline" size="icon">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-2 mt-2">
                      {formData.documents.map((url, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-muted rounded">
                          <LinkIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm flex-1 truncate">{url}</span>
                          <button onClick={() => removeDocument(i)} className="text-red-500">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700 text-sm">
                      You're almost done! Once submitted, our team will review your problem and reach out if we need more information.
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation buttons */}
        <div className="mt-6 flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            size="lg"
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          
          <div className="text-sm text-muted-foreground">
            Step {currentStep} of {totalSteps}
          </div>

          {currentStep < totalSteps ? (
            <Button
              onClick={handleNext}
              size="lg"
              className="gap-2 bg-blue-600 hover:bg-blue-700"
              disabled={!isStepValid()}
            >
              Next
              <ArrowLeft className="h-4 w-4 rotate-180" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !isStepValid()}
              size="lg"
              className="gap-2 bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? (
                <>Submitting...</>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Submit Problem
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Add missing imports
import { Plus, X, Link as LinkIcon } from 'lucide-react';